/** @jsxImportSource react */
import React from 'react';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Help as HelpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { BooleanState, SafetyState } from './types';

/**
 * Returns display state for 3-state boolean fields (null/true/false)
 * Used for: Canadian citizen, passport valid, etc.
 */
export const getBooleanState = (value: boolean | null): BooleanState => {
  if (value === null || value === undefined) {
    return {
      icon: <HelpIcon sx={{ color: '#757575' }} />,
      text: 'Not specified',
      color: '#fafafa',
    };
  } else if (value === true) {
    return {
      icon: <CheckIcon color="success" />,
      text: 'Yes',
      color: '#fafafa',
    };
  } else {
    return {
      icon: <CancelIcon sx={{ color: '#757575' }} />,
      text: 'No',
      color: '#fafafa',
    };
  }
};

/**
 * Returns display state for safety fields where TRUE is BAD, FALSE is GOOD
 * Used for: accidents, tickets, drug testing
 */
export const getSafetyState = (
  value: boolean | null,
  trueLabel: string = 'Yes',
  falseLabel: string = 'No'
): SafetyState => {
  if (value === null || value === undefined) {
    return {
      icon: <HelpIcon sx={{ color: '#757575' }} />,
      text: 'Not specified',
      color: '#fafafa',
      borderColor: '#e0e0e0',
      textColor: '#757575',
    };
  } else if (value === true) {
    return {
      icon: <WarningIcon color="error" />,
      text: trueLabel,
      color: '#fafafa',
      borderColor: '#e0e0e0',
      textColor: '#1976d2',
    };
  } else {
    return {
      icon: <CheckIcon color="success" />,
      text: falseLabel,
      color: '#fafafa',
      borderColor: '#e0e0e0',
      textColor: '#1976d2',
    };
  }
};

/**
 * Converts experience radius display text to enum value
 */
export const convertRadiusToEnum = (displayValue: string): string => {
  const mapping: Record<string, string> = {
    'Up to 80km': 'UP_TO_80KM',
    'Up to 160km': 'UP_TO_160KM',
    'Up to 500km': 'UP_TO_500KM',
    'Up to 1000km': 'UP_TO_1000KM',
    'Long Haul (1000km+)': 'MORE_THAN_1000KM',
    '': 'NOT_APPLICABLE',
  };
  return mapping[displayValue] || 'NOT_APPLICABLE';
};

/**
 * Converts experience radius enum to display text
 */
export const convertEnumToRadius = (enumValue: string): string => {
  const mapping: Record<string, string> = {
    UP_TO_80KM: 'Up to 80km',
    UP_TO_160KM: 'Up to 160km',
    UP_TO_500KM: 'Up to 500km',
    UP_TO_1000KM: 'Up to 1000km',
    MORE_THAN_1000KM: 'Long Haul (1000km+)',
    NOT_APPLICABLE: '',
  };
  return mapping[enumValue] || '';
};

/**
 * Parse comma-separated string into array of values
 */
export const parseMultiValue = (value: string | undefined): string[] => {
  if (!value) return [];
  return value.split(',').map(v => v.trim()).filter(v => v.length > 0);
};

/**
 * Convert array of values to comma-separated string
 */
export const stringifyMultiValue = (values: string[]): string => {
  return values.join(', ');
};

/**
 * Format date for display (YYYY-MM-DD format)
 */
export const formatDate = (date: string | Date | undefined): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

/**
 * Parse date string to ISO format
 */
export const parseDate = (dateString: string): string => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toISOString().split('T')[0];
};
